import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchBySlug } from '../lib/api';
import { ShieldCheck, Award, ThumbsUp, Clock, UserCheck, Truck } from 'lucide-react';

export default function About() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function loadData() {
      const data = await fetchBySlug('da', 'om-os');
      setPageData(data);
    }
    loadData();
  }, []);

  if (!pageData) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <div className="animate-pulse text-sm font-semibold tracking-widest uppercase">Henter indhold...</div>
      </div>
    );
  }

  // Icons mapping for the advantages
  const advantages = [
    { text: 'Hurtig ekspedition fra ordre til færdigt arbejde', icon: Clock },
    { text: 'Kvalitetsarbejde til konkurrencedygtige priser', icon: ThumbsUp },
    { text: 'Kun behov for én kontaktperson til én eller flere opgaver', icon: UserCheck },
    { text: 'Arbejdet udføres i henhold til alle bestemmelser for VVS-arbejdet', icon: ShieldCheck },
    { text: 'Velkvalificeret ansvarsbevidste medarbejdere med fagligt stolthed', icon: Award },
    { text: 'Veludrustede servicebiler til rådighed', icon: Truck }
  ];

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title || 'Om os – Møldrup VVS A/S'}</title>
        <meta name="description" content={pageData.meta_description || ''} />
        <html lang="da" />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative bg-slate-950 text-white py-24 md:py-32 border-b border-slate-900">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/0cd8fe7f-0ba4-47e3-908a-4fc623bd309c.jpg"
            alt="Om os"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-12">
          <span className="text-blue-500 text-xs font-extrabold tracking-[0.25em] uppercase block mb-3">
            Hvem vi er
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-wider uppercase">
            Om Møldrup VVS A/S
          </h1>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-24 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Advantages List */}
            <div className="lg:col-span-7 space-y-8">
              <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
                Dine fordele
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-wider uppercase">
                Hvorfor vælge Møldrup VVS?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                {advantages.map((adv, idx) => {
                  const Icon = adv.icon;
                  return (
                    <div key={idx} className="flex items-start space-x-3 p-4 bg-slate-50 border border-gray-100 rounded-none">
                      <Icon className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-800 leading-relaxed">
                        {adv.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Sales Circle Image */}
            <div className="lg:col-span-5 flex justify-center">
              {pageData.images && pageData.images.length > 0 && (
                <div className="relative max-w-sm w-full aspect-square bg-slate-50 border border-gray-100 p-8 flex items-center justify-center shadow-lg">
                  <img
                    src={pageData.images[0]}
                    alt="Møldrup VVS fordele"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Narrative History Section */}
      <section className="py-24 bg-slate-900 text-white border-t border-slate-950">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
          <span className="text-blue-500 text-xs font-extrabold tracking-[0.25em] uppercase block">
            Vores værdier
          </span>
          <h2 className="text-2xl md:text-4xl font-extrabold tracking-wider uppercase">
            Kvalitetsbevidst & Troværdig
          </h2>
          <div className="w-12 h-1 bg-blue-500 mx-auto" />
          <p className="text-gray-300 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
            "Vores hensigt er altid at fremstå kvalitetbevidste og troværdige. Vi er en VVS-virksomhed med et højt serviceniveau og vi leverer kvalitetsarbejde til en fordelagtig pris og til den aftalte tid."
          </p>
        </div>
      </section>
    </>
  );
}
